import classNames from "classnames";
import { isToday } from "date-fns";
import { observer } from "mobx-react";
import React, { useState } from "react";
import { FormattedDate, FormattedTime } from "react-intl";
import formStyles from "@fidget/common/components/form/form.css";
import { ClickAwayListener } from "@fidget/common/components/form/clickAwayListener";
import { intl } from "@fidget/common/i18n/TypedIntl";
import editStyles from "./dateTimeDropdown.css";

interface Props {
  startingPoint: Date;
  mode: DatetimeDropdownMode;
  onChange?: (option: Date) => void;
  options: Array<Date>;
  inputRef?: (el?: HTMLLIElement) => void;
  id?: string;
  height?: string;
  marginBottom?: string;
  width?: string;
}

export enum DatetimeDropdownMode {
  DAY = "DAY",
  TIME = "TIME",
  TIME_WITH_DURATION = "TIME_WITH_DURATION",
  NONE = "NONE"
}

export const DateTimeDropdown = observer(
  ({
    startingPoint,
    mode,
    onChange,
    options,
    inputRef,
    id,
    marginBottom,
    height,
    width
  }: Props) => {
    const [dropdownVisible, setDropdownVisible] = useState(false);

    const handleChoice = (option: Date) => {
      setDropdownVisible(false);
      if (onChange) {
        onChange(option);
      }
    };

    const renderByMode = (option: Date, idx: number) => {
      switch (mode) {
        case DatetimeDropdownMode.TIME:
          return (
            <div className={editStyles.suggestion}>
              <div className={editStyles.main}>
                <FormattedTime value={option} />
              </div>
            </div>
          );
        case DatetimeDropdownMode.TIME_WITH_DURATION:
          return (
            <div className={editStyles.suggestion}>
              <div className={editStyles.main}>
                <FormattedTime value={option} />
              </div>
              <div className={editStyles.sub}>{`${(idx + 1) * 15}m`}</div>
            </div>
          );
        case DatetimeDropdownMode.DAY:
          return (
            <div className={editStyles.suggestion}>
              <div className={editStyles.main}>
                <FormattedDate value={option} day={"2-digit"} month={"2-digit"} />
              </div>
              <div className={editStyles.sub}>
                {isToday(option) ? (
                  intl.formatMessage("date.today")
                ) : (
                  <FormattedDate value={option} weekday={"long"} />
                )}
              </div>
            </div>
          );
        default:
          return <div />;
      }
    };

    const renderLabelByMode = () => {
      switch (mode) {
        case DatetimeDropdownMode.TIME:
          return <FormattedTime value={startingPoint} />;
        case DatetimeDropdownMode.TIME_WITH_DURATION:
          return <FormattedTime value={startingPoint} />;
        case DatetimeDropdownMode.DAY:
          return <FormattedDate value={startingPoint} day={"2-digit"} month={"2-digit"} />;
        default:
          return <div />;
      }
    };

    const checkCurrentChoice = (option: Date) => {
      const isCurrentTime = startingPoint.getTime() === option.getTime();
      const isCurrentDate =
        startingPoint.getDate() === option.getDate() &&
        startingPoint.getMonth() === option.getMonth();
      return mode === DatetimeDropdownMode.DAY ? isCurrentDate : isCurrentTime;
    };

    const renderCompletions = () => {
      return (
        <ul className={formStyles["completion-list"]}>
          {options.map((option, idx) => (
            <li
              key={idx}
              className={checkCurrentChoice(option) ? editStyles["current-choice"] : ""}
              onClick={(ev) => {
                ev.stopPropagation();
                handleChoice(option);
              }}
              ref={(el) => inputRef && inputRef(el || undefined)}
            >
              {renderByMode(option, idx)}
            </li>
          ))}
        </ul>
      );
    };

    return (
      <ClickAwayListener
        onClickAway={() => setDropdownVisible(false)}
        className={classNames(editStyles["time-input"], { [editStyles.focus]: dropdownVisible })}
        style={{ width, height, marginBottom }}
      >
        <div
          className={editStyles.row}
          onClick={() => setDropdownVisible(!dropdownVisible)}
          id={id}
        >
          {renderLabelByMode()}
          <i className={classNames(editStyles["icon-arrow"], "icon-arrow")} />
        </div>
        {dropdownVisible && (
          <div className={formStyles["completion-container"]}>
            <div className={formStyles["completion-dropdown"]}>
              {options.length !== 0 && (
                <div className={editStyles["time-list-container"]}>{renderCompletions()}</div>
              )}
            </div>
          </div>
        )}
      </ClickAwayListener>
    );
  }
);
